import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
  useReportSubjectPlacement,
} from "../../../lib/toolPrepImageLayout";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import SoftWatermarkOverlay from "../../../shared/Popup/SoftWatermarkOverlay";
import "../../../shared/Popup/ToolPrepStage.css";
import "./AiFeaturePreview.css";

/**
 * Shared right-side preview for AI feature prep tools.
 * After cutout, BB hugs opaque subject (Editor-like) with move / resize.
 */
const AiFeaturePreview = ({
  imageUrl = null,
  loading = false,
  loadingLabel = "Working…",
  emptyLabel = "Upload an image to preview",
  transparent = false,
  imageFilter = null,
  filterBleedPx = 0,
  showSubjectBox = false,
  showSoftWatermark = false,
  softWatermarkToolKey = "editing",
}) => {
  const rootRef = useRef(null);
  const naturalSize = useImageNaturalSize(imageUrl);
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const bleed = Math.max(0, Math.round(Number(filterBleedPx) || 0));
  const hasFilter = Boolean(imageFilter) && imageFilter !== "none";

  const fitFrame = useMemo(() => {
    if (!imageUrl || !(naturalSize.width > 0) || !(naturalSize.height > 0)) {
      return null;
    }
    const boxW = stageSize.width;
    const boxH = stageSize.height;
    if (!(boxW > 0) || !(boxH > 0)) return null;
    const scale = Math.min(boxW / naturalSize.width, boxH / naturalSize.height);
    const width = naturalSize.width * scale;
    const height = naturalSize.height * scale;
    return {
      x: (boxW - width) / 2 + (hasFilter ? bleed : 0),
      y: (boxH - height) / 2 + (hasFilter ? bleed : 0),
      width,
      height,
    };
  }, [
    bleed,
    hasFilter,
    imageUrl,
    naturalSize.height,
    naturalSize.width,
    stageSize.height,
    stageSize.width,
  ]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !loading;
  const [isCropping, setIsCropping] = useState(false);
  const {
    object,
    setObject,
    wrapperStyle,
    imageStyle,
    contentCrop,
    crop,
    cropExtent,
    originalImageSize,
  } = useToolPrepSubjectObject({
    enabled: interactive,
    imageUrl,
    fitFrame,
    resetKey: imageUrl,
  });

  const placementStage = useMemo(
    () => ({
      width: stageSize.width + (hasFilter ? bleed * 2 : 0),
      height: stageSize.height + (hasFilter ? bleed * 2 : 0),
    }),
    [bleed, hasFilter, stageSize.height, stageSize.width]
  );

  useReportSubjectPlacement(
    null,
    object,
    contentCrop,
    placementStage,
    imageUrl
  );

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const measure = () => {
      const aspect =
        imageUrl && naturalSize.width > 0 && naturalSize.height > 0
          ? naturalSize.width / naturalSize.height
          : 1;
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        aspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [imageUrl, loading, naturalSize.width, naturalSize.height]);

  return (
    <ToolPrepCanvasStage
      className="aftp-preview"
      ref={rootRef}
      metaImageUrl={!loading ? imageUrl : null}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      {loading ? (
        <div className="aftp-preview__loader">
          <RoundLoader />
          <p>{loadingLabel}</p>
        </div>
      ) : null}
      {!loading && !imageUrl ? (
        <div
          className="aftp-preview__empty-stage"
          style={{
            width: stageSize.width,
            height: stageSize.height,
          }}
          aria-label="Empty preview"
        >
          <p className="aftp-preview__empty">{emptyLabel}</p>
        </div>
      ) : null}
      {!loading && imageUrl ? (
        <div
          className={`aftp-preview__frame${
            transparent ? " aftp-preview__frame--transparent" : ""
          }${hasFilter ? " aftp-preview__frame--filter" : ""}${
            showSubjectBox ? " aftp-preview__frame--subject-bb" : ""
          }`}
          style={{
            width: stageSize.width + (hasFilter ? bleed * 2 : 0),
            height: stageSize.height + (hasFilter ? bleed * 2 : 0),
            padding: hasFilter ? bleed : undefined,
            position: "relative",
          }}
        >
          <div className="tool-prep-stage__clip">
            {interactive && object && wrapperStyle ? (
              <div
                className="aftp-preview__subject"
                style={subjectWrapperStyleForCrop(
                  {
                    ...wrapperStyle,
                    overflow: hasFilter ? "visible" : "hidden",
                  },
                  isCropping
                )}
              >
                <ToolPrepSubjectCropGhost
                  active={isCropping}
                  src={imageUrl}
                  style={imageStyle}
                />
                <img
                  src={imageUrl}
                  alt="Tool preview"
                  draggable={false}
                  className="aftp-preview__img--absolute"
                  style={subjectMainImageStyleForCrop(
                    {
                      ...imageStyle,
                      ...(hasFilter ? { filter: imageFilter } : null),
                    },
                    { isCropping, crop, originalImageSize, object }
                  )}
                />
              </div>
            ) : (
              <img
                src={imageUrl}
                alt="Tool preview"
                draggable={false}
                style={hasFilter ? { filter: imageFilter } : undefined}
              />
            )}
          </div>
          {interactive && object && wrapperStyle ? (
            <div className="tool-prep-stage__chrome">
              <ToolPrepSubjectBoundingBox
                enabled
                interactive
                frame={object}
                crop={crop}
                cropExtent={cropExtent}
                originalImageSize={originalImageSize}
                onChange={setObject}
                onCroppingChange={setIsCropping}
                stageSize={{
                  width: stageSize.width + (hasFilter ? bleed * 2 : 0),
                  height: stageSize.height + (hasFilter ? bleed * 2 : 0),
                }}
              />
            </div>
          ) : null}
          {showSoftWatermark ? (
            <SoftWatermarkOverlay toolKey={softWatermarkToolKey} />
          ) : null}
        </div>
      ) : null}
    </ToolPrepCanvasStage>
  );
};

export default AiFeaturePreview;
